import { useEffect, useRef, useState } from 'react';
import { keccak_256 } from '@noble/hashes/sha3';
import { api, ApiError, apiPairWCFromShell, apiListWCSessions, apiDisconnectWCSession, openWCRequestStream, type WCSessionDTO, type WCSigningRequestEvent } from '../api/client';
import { startListener } from '../transport/listener';
import type { InitRequest, ResponseMessage } from '../transport/protocol';
import { secp256k1 } from '@noble/curves/secp256k1';
import {
  addressFromPublicKey,
  base64Decode,
  base64Encode,
  bytesToHex,
  combineShares,
  generateKeypair,
  hexToBytes,
  signDigest,
  splitKey,
  wipe,
} from '../crypto/wallet';
import { generateEd25519Keypair, solanaAddress } from '../crypto/ed25519';
import { curvesForNetworks, getNetwork, deriveAddress, type Curve } from '../crypto/networks';
import { signForNetwork } from '../crypto/networkSign';
import { hashTypedData } from '../crypto/typedData';
import { signEip1559Tx } from '../crypto/eip1559';
import {
  clearDeviceShare,
  clearSessionToken,
  readDeviceShare,
  readSessionToken,
  writeDeviceShare,
  writeSessionToken,
} from '../storage/deviceShare';
import type { IframeState, PendingSign, WalletInfo } from './state';
import { OtpEmailScreen, OtpCodeScreen } from '../features/otp/OtpScreens';
import { ConfirmScreen } from '../features/confirm/ConfirmScreen';
import { WalletScreen } from '../features/wallet/WalletScreen';
import { CreateWalletScreen } from '../features/wallet/CreateWalletScreen';
import { RestoreStartScreen, RestoreCodeScreen } from '../features/recovery/RestoreScreens';

export function App() {
  const [state, setState] = useState<IframeState>({ phase: 'waiting' });
  const stateRef = useRef(state);
  stateRef.current = state;

  // pendingByRequestId resolves when the user approves a sign request.
  const pendingRef = useRef<Map<string, PendingSign>>(new Map());

  // Sign requests are serialised. If a previous sign is still in flight
  // (confirming or signing) and a new one arrives, instead of returning
  // `not_ready` immediately, we wait until phase returns to 'idle' and then
  // process the new request. This makes the SDK robust to React strict
  // mode double-invocation and to callers that fire-and-forget without
  // awaiting the previous Promise.
  const signQueueRef = useRef<Promise<void>>(Promise.resolve());

  // waitForIdle resolves the next time the iframe is back in 'idle' (and
  // therefore safe to start a new sign). Bounded at 60s to avoid a stuck
  // request blocking the listener forever.
  function waitForIdle(maxMs = 60_000): Promise<void> {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const tick = () => {
        if (stateRef.current.phase === 'idle') return resolve();
        if (Date.now() - started > maxMs) return reject(new Error('timed out waiting for previous sign'));
        setTimeout(tick, 50);
      };
      tick();
    });
  }

  // serialiseSign queues `fn` after the current sign's promise so concurrent
  // requests are processed one at a time. The returned Promise resolves with
  // whatever `fn` resolves.
  function serialiseSign<T>(fn: () => Promise<T>): Promise<T> {
    const prev = signQueueRef.current;
    const next = prev.then(async () => {
      try { await waitForIdle(); } catch { /* swallow — fn will fail with not_ready below */ }
    });
    signQueueRef.current = next.catch(() => {});
    return next.then(fn);
  }

  useEffect(() => {
    const listener = startListener(async (req, _source) => {
      switch (req.type) {
        case 'init':
          await handleInit(req, setState);
          return {
            type: 'ready' as const,
            requestId: req.requestId,
            address: currentAddress(stateRef.current),
            signerAddress: currentSigner(stateRef.current),
            wallets: currentWallets(stateRef.current),
          };
        case 'getAddress':
          return {
            type: 'ready' as const,
            requestId: req.requestId,
            address: currentAddress(stateRef.current),
            signerAddress: currentSigner(stateRef.current),
            wallets: currentWallets(stateRef.current),
          };
        case 'signMessage': {
          return serialiseSign(async () => {
          const cur = stateRef.current;
          if (cur.phase !== 'idle') {
            return { type: 'error' as const, requestId: req.requestId, code: 'not_ready', message: `wallet not ready (phase: ${cur.phase})` };
          }
          const msgCurve = req.network ? networkToCurve(req.network) : 'secp256k1';
          return new Promise<ResponseMessage>((resolve) => {
            const pending: PendingSign = {
              requestId: req.requestId,
              kind: 'message',
              message: req.message,
              curve: msgCurve,
              network: req.network,
              resolve: ({ signature, digest }) =>
                resolve({ type: 'signed', requestId: req.requestId, signature, digest }),
              reject: (reason) =>
                resolve({ type: 'error', requestId: req.requestId, code: 'rejected', message: reason }),
            };
            pendingRef.current.set(req.requestId, pending);
            setState({
              phase: 'confirming',
              ctx: cur.ctx,
              identity: cur.identity,
              address: cur.address,
              signerAddress: cur.signerAddress,
              pending,
            });
          });
          });
        }
        case 'signTypedData': {
          return serialiseSign(async () => {
          const cur = stateRef.current;
          if (cur.phase !== 'idle') {
            return { type: 'error' as const, requestId: req.requestId, code: 'not_ready', message: `wallet not ready (phase: ${cur.phase})` };
          }
          return new Promise<ResponseMessage>((resolve) => {
            const pending: PendingSign = {
              requestId: req.requestId,
              kind: 'typedData',
              typedData: req.typedData,
              resolve: ({ signature, digest }) =>
                resolve({ type: 'signed', requestId: req.requestId, signature, digest }),
              reject: (reason) =>
                resolve({ type: 'error', requestId: req.requestId, code: 'rejected', message: reason }),
            };
            pendingRef.current.set(req.requestId, pending);
            setState({
              phase: 'confirming',
              ctx: cur.ctx,
              identity: cur.identity,
              address: cur.address,
              signerAddress: cur.signerAddress,
              pending,
            });
          });
          });
        }
        case 'sendTransaction': {
          return serialiseSign(async () => {
            const cur = stateRef.current;
            if (cur.phase !== 'idle') {
              return { type: 'error' as const, requestId: req.requestId, code: 'not_ready', message: `wallet not ready (phase: ${cur.phase})` };
            }
            return runSendTransaction(req, cur.ctx, cur.identity, cur.address, cur.signerAddress, setState, pendingRef);
          });
        }
        case 'enableSmartAccount': {
          return serialiseSign(async () => {
            const cur = stateRef.current;
            if (cur.phase !== 'idle') {
              return { type: 'error' as const, requestId: req.requestId, code: 'not_ready', message: `wallet not ready (phase: ${cur.phase})` };
            }
            return runEnableSmartAccount(req, cur.ctx);
          });
        }
        case 'listSmartAccounts': {
          return serialiseSign(async () => {
            const cur = stateRef.current;
            if (cur.phase !== 'idle') {
              return { type: 'error' as const, requestId: req.requestId, code: 'not_ready', message: `wallet not ready (phase: ${cur.phase})` };
            }
            return runListSmartAccounts(req, cur.ctx);
          });
        }
        case 'pairWC': {
          return serialiseSign(async () => {
            const cur = stateRef.current;
            if (cur.phase !== 'idle') {
              return { type: 'error' as const, requestId: req.requestId, code: 'not_ready', message: `wallet not ready (phase: ${cur.phase})` };
            }
            return runPairWC(req, cur.ctx);
          });
        }
        case 'listWCSessions': {
          return serialiseSign(async () => {
            const cur = stateRef.current;
            if (cur.phase !== 'idle') {
              return { type: 'error' as const, requestId: req.requestId, code: 'not_ready', message: `wallet not ready (phase: ${cur.phase})` };
            }
            return runListWCSessions(req, cur.ctx);
          });
        }
        case 'disconnectWC': {
          return serialiseSign(async () => {
            const cur = stateRef.current;
            if (cur.phase !== 'idle') {
              return { type: 'error' as const, requestId: req.requestId, code: 'not_ready', message: `wallet not ready (phase: ${cur.phase})` };
            }
            return runDisconnectWC(req, cur.ctx);
          });
        }
        case 'logout': {
          // Clear the session token only. Device shares stay in localStorage —
          // they're namespaced by user_identity_id and only become reachable
          // again when the same user signs in. A different user gets a fresh
          // identity with no shares, so cross-user leakage is not possible.
          const cur = stateRef.current;
          if (cur.phase !== 'waiting' && cur.phase !== 'auth_otp_email' && cur.phase !== 'auth_otp_code') {
            const ctx = phaseHasCtx(cur) ? cur.ctx : null;
            if (ctx) clearSessionToken(ctx.organizationId);
            if (ctx && ctx.authMode === 'sigil') {
              setState({ phase: 'auth_otp_email', ctx });
            } else {
              setState({ phase: 'waiting' });
            }
          }
          return { type: 'logged_out', requestId: req.requestId };
        }
        default: {
          // Unknown message type. Echo the requestId back so the SDK
          // transport correlates the error to the original send and
          // rejects immediately (otherwise it would just time out at 30s).
          const r = req as { requestId?: string; type?: string };
          return {
            type: 'error',
            requestId: r.requestId ?? '',
            code: 'unknown_type',
            message: `unknown message type: ${r.type ?? ''}`,
          };
        }
      }
    });
    return () => listener.destroy();
  }, []);

  // Open a WC SSE subscription as soon as the iframe is past auth.
  // Re-runs when the session changes (login / logout / org switch),
  // NOT on every confirm/sign phase flip — re-opening tears down the
  // EventSource, the backend's StreamRequests re-fetches the pending
  // backlog, and a request that's mid-approval looks "still pending"
  // until the iframe's POST /sign lands. That replay would queue
  // behind the in-flight sign and re-prompt the user with the same
  // request right after they signed it.
  const sseCleanupRef = useRef<(() => void) | null>(null);
  const sseSessionKey = (() => {
    if (!phaseHasCtx(state)) return '';
    if (state.phase === 'auth_otp_email' || state.phase === 'auth_otp_code') return '';
    if (state.phase === 'auth_oidc') return '';
    return `${state.ctx.organizationId}:${state.identity.userIdentityID}`;
  })();
  useEffect(() => {
    sseCleanupRef.current?.();
    sseCleanupRef.current = null;
    if (!sseSessionKey) return;

    const s = stateRef.current;
    if (!phaseHasCtx(s)) return;
    const ctx = s.ctx;
    const token = readSessionToken(ctx.organizationId);
    if (!token) return;

    const cleanup = openWCRequestStream(
      { publishableKey: ctx.publishableKey, token },
      (req) => {
        // Queue behind any in-flight sign so the Confirm screen
        // doesn't get overwritten. waitForIdle inside serialiseSign
        // gates the actual show-and-sign.
        void serialiseSign(() => runWCRequest(req, ctx, pendingRef, setState, stateRef));
      },
    );
    sseCleanupRef.current = cleanup;
    return () => {
      cleanup();
      sseCleanupRef.current = null;
    };
  }, [sseSessionKey]);

  return <Screen state={state} setState={setState} />;
}

let cachedWallets: WalletInfo[] = [];
let orgSupportedNetworks: string[] = ['ethereum'];
let orgSigningMode: 'client' | 'tee' = 'client';

function currentAddress(s: IframeState): string | null {
  if (s.phase === 'idle' || s.phase === 'confirming' || s.phase === 'signing') return s.address;
  return null;
}

function currentSigner(s: IframeState): string | null {
  if (s.phase === 'idle' || s.phase === 'confirming' || s.phase === 'signing') {
    return s.signerAddress ?? null;
  }
  return null;
}

function currentWallets(_s: IframeState): WalletInfo[] | undefined {
  return cachedWallets.length > 0 ? cachedWallets : undefined;
}

function networkToCurve(network: string): Curve {
  const net = getNetwork(network);
  return net ? net.curve : 'secp256k1';
}

function phaseHasCtx(s: IframeState): s is Exclude<IframeState, { phase: 'waiting' } | { phase: 'error' }> {
  return s.phase !== 'waiting' && s.phase !== 'error';
}

async function handleInit(req: InitRequest, setState: (s: IframeState) => void) {
  const ctx = {
    publishableKey: req.publishableKey,
    organizationId: req.organizationId,
    authMode: req.authMode,
  };

  if (req.authMode === 'oidc') {
    if (!req.token) {
      setState({ phase: 'error', message: 'OIDC mode requires a token from the parent app.' });
      return;
    }
    setState({ phase: 'auth_oidc', ctx });
    try {
      const identity = await api.identify({ publishableKey: ctx.publishableKey, token: req.token });
      if (identity.supported_networks) orgSupportedNetworks = identity.supported_networks;
      orgSigningMode = identity.signing_mode === 'tee' ? 'tee' : 'client';
      writeSessionToken(ctx.organizationId, req.token);
      await bootstrapWallet(setState, ctx, { userIdentityID: identity.user_identity_id, email: identity.email }, req.token);
    } catch (e) {
      setState({ phase: 'error', message: e instanceof Error ? e.message : 'auth failed' });
    }
    return;
  }

  // sigil-hosted: try to reuse a Sigil token from sessionStorage; otherwise show OTP.
  const cached = readSessionToken(ctx.organizationId);
  if (cached) {
    try {
      const identity = await api.identify({ publishableKey: ctx.publishableKey, token: cached });
      if (identity.supported_networks) orgSupportedNetworks = identity.supported_networks;
      orgSigningMode = identity.signing_mode === 'tee' ? 'tee' : 'client';
      await bootstrapWallet(setState, ctx, { userIdentityID: identity.user_identity_id, email: identity.email }, cached);
      return;
    } catch {
      /* token expired/invalid; fall through to OTP */
    }
  }
  setState({ phase: 'auth_otp_email', ctx });
}

async function bootstrapWallet(
  setState: (s: IframeState) => void,
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
  identity: { userIdentityID: string; email?: string },
  token: string,
) {
  setState({ phase: 'bootstrap', ctx, identity });
  const apiCtx = { publishableKey: ctx.publishableKey, token };
  try {
    const wallets = await api.getAllWallets(apiCtx);
    if (wallets.length === 0) {
      setState({ phase: 'no_wallet', ctx, identity });
      return;
    }
    cachedWallets = [];
    for (const w of wallets) {
      const curve = w.curve ?? 'secp256k1';
      const pubBytes = hexToBytes(w.public_key);
      for (const net of orgSupportedNetworks) {
        const netDef = getNetwork(net);
        if (!netDef || netDef.curve !== curve) continue;
        cachedWallets.push({
          network: net,
          curve,
          address: deriveAddress(net, pubBytes),
          publicKey: w.public_key,
        });
      }
    }
    const primary = wallets.find(w => w.curve === 'secp256k1') ?? wallets[0];

    // TEE mode: the TEE holds both shares — no device share needed.
    if (orgSigningMode === 'tee') {
      setState({
        phase: 'idle', ctx, identity,
        address: primary.address,
        signerAddress: primary.signer_address,
      });
      return;
    }

    // Client mode: check device shares exist for all wallets.
    const allSharesPresent = wallets.every(
      w => readDeviceShare(ctx.organizationId, identity.userIdentityID, w.curve),
    );
    if (allSharesPresent) {
      setState({
        phase: 'idle', ctx, identity,
        address: primary.address,
        signerAddress: primary.signer_address,
      });
    } else {
      setState({ phase: 'wallet_no_device_share', ctx, identity, address: primary.address });
    }
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      setState({ phase: 'no_wallet', ctx, identity });
      return;
    }
    setState({ phase: 'error', message: e instanceof Error ? e.message : 'bootstrap failed' });
  }
}

function Screen({ state, setState }: { state: IframeState; setState: (s: IframeState) => void }) {
  if (state.phase === 'waiting') {
    return <CenteredMessage title="Sigil Wallet" body="Waiting for the host app…" />;
  }
  if (state.phase === 'error') {
    return <CenteredMessage title="Something went wrong" body={state.message} tone="error" />;
  }
  if (state.phase === 'auth_oidc' || state.phase === 'bootstrap') {
    return <CenteredMessage title="Signing you in…" body="Validating your identity." />;
  }
  if (state.phase === 'auth_otp_email') {
    return (
      <OtpEmailScreen
        onSubmit={async (email) => {
          await api.startOtp({ publishableKey: state.ctx.publishableKey, token: null }, email);
          setState({ phase: 'auth_otp_code', ctx: state.ctx, email });
        }}
      />
    );
  }
  if (state.phase === 'auth_otp_code') {
    return (
      <OtpCodeScreen
        email={state.email}
        onSubmit={async (code) => {
          const res = await api.verifyOtp({ publishableKey: state.ctx.publishableKey, token: null }, state.email, code);
          writeSessionToken(state.ctx.organizationId, res.token);
          const identity = await api.identify({ publishableKey: state.ctx.publishableKey, token: res.token });
          if (identity.supported_networks) orgSupportedNetworks = identity.supported_networks;
          orgSigningMode = identity.signing_mode === 'tee' ? 'tee' : 'client';
          await bootstrapWallet(setState, state.ctx, { userIdentityID: res.user_identity_id, email: res.email }, res.token);
        }}
        onResend={async () => {
          await api.startOtp({ publishableKey: state.ctx.publishableKey, token: null }, state.email);
        }}
      />
    );
  }
  if (state.phase === 'no_wallet') {
    return (
      <CreateWalletScreen
        onCreate={async () => {
          setState({ phase: 'creating', ctx: state.ctx, identity: state.identity });
          if (orgSigningMode === 'tee') {
            await createWalletFlowTEE(setState, state.ctx, state.identity);
          } else {
            const curves = curvesForNetworks(orgSupportedNetworks);
            await createWalletFlow(setState, state.ctx, state.identity, curves.length > 0 ? curves : ['secp256k1']);
          }
        }}
      />
    );
  }
  if (state.phase === 'creating') {
    return <CenteredMessage title="Creating wallet…" body="Splitting key material across shares." />;
  }
  if (state.phase === 'wallet_no_device_share') {
    return (
      <RestoreStartScreen
        email={state.identity.email}
        onStart={async () => {
          const token = readSessionToken(state.ctx.organizationId);
          if (!token) {
            setState({ phase: 'error', message: 'session expired' });
            return;
          }
          const res = await api.initiateRecovery({ publishableKey: state.ctx.publishableKey, token });
          setState({
            phase: 'recovery_email_sent',
            ctx: state.ctx,
            identity: state.identity,
            address: state.address,
            processId: res.recovery_process_id,
          });
        }}
      />
    );
  }
  if (state.phase === 'recovery_email_sent') {
    return (
      <RestoreCodeScreen
        email={state.identity.email}
        onSubmit={async (code) => {
          await runRecoveryFlow(setState, state.ctx, state.identity, state.address, state.processId, code);
        }}
      />
    );
  }
  if (state.phase === 'recovery_re_sharing') {
    return <CenteredMessage title="Restoring wallet…" body="Reconstructing key and rotating shares." />;
  }
  if (state.phase === 'idle') {
    return <WalletScreen address={state.address} email={state.identity.email} />;
  }
  if (state.phase === 'confirming') {
    const pendingNetwork = ('network' in state.pending) ? state.pending.network : undefined;
    const pendingCurve = ('curve' in state.pending && state.pending.curve) ? state.pending.curve : 'secp256k1';
    const matchedWallet = pendingNetwork
      ? cachedWallets.find(w => w.network === pendingNetwork)
      : cachedWallets.find(w => w.curve === pendingCurve);
    const confirmAddress = matchedWallet?.address ?? state.address;
    return (
      <ConfirmScreen
        address={confirmAddress}
        pending={state.pending}
        onApprove={async () => {
          const dbg = (event: string, extra?: Record<string, unknown>) => {
            try {
              console.log(`[sigil-iframe] ${event}`, { kind: state.pending.kind, requestId: state.pending.requestId, ...extra });
              window.parent?.postMessage(
                { type: 'iframe_debug', event, kind: state.pending.kind, requestId: state.pending.requestId, ...extra },
                '*',
              );
            } catch { /* never block */ }
          };
          dbg('onApprove_entered');
          // The 'rawTx' and 'userOp' kinds do their own signing inside
          // the runSendTransaction / runUserOpTransaction state machines
          // *after* the user approves. Here we just unblock the await
          // that's gating on the pending promise; the run function then
          // reconstructs the key, signs, submits, and finally sets the
          // iframe back to 'idle'. Don't call signWithReconstruction for
          // these — it explicitly rejects them.
          if (state.pending.kind === 'rawTx' || state.pending.kind === 'userOp' || state.pending.kind === 'wc') {
            dbg('approval_for_run_flow', { kind: state.pending.kind });
            state.pending.resolve({ signature: '' });
            return;
          }
          setState({
            phase: 'signing',
            ctx: state.ctx,
            identity: state.identity,
            address: state.address,
            signerAddress: state.signerAddress,
            pending: state.pending,
          });
          try {
            const pendingCurve: Curve = ('curve' in state.pending && state.pending.curve) ? state.pending.curve as Curve : 'secp256k1';
            dbg('signWithReconstruction_started', { curve: pendingCurve });
            const out = await signWithReconstruction(state.ctx, state.identity, state.pending, state.signerAddress, pendingCurve);
            dbg('signWithReconstruction_completed');
            state.pending.resolve(out);
            setState({ phase: 'idle', ctx: state.ctx, identity: state.identity, address: state.address, signerAddress: state.signerAddress });
          } catch (e) {
            dbg('signWithReconstruction_failed', { error: e instanceof Error ? e.message : String(e) });
            state.pending.reject(e instanceof Error ? e.message : 'signing failed');
            setState({ phase: 'idle', ctx: state.ctx, identity: state.identity, address: state.address, signerAddress: state.signerAddress });
          }
        }}
        onReject={() => {
          state.pending.reject('user rejected');
          setState({ phase: 'idle', ctx: state.ctx, identity: state.identity, address: state.address, signerAddress: state.signerAddress });
        }}
      />
    );
  }
  if (state.phase === 'signing') {
    return <CenteredMessage title="Signing…" body="Reconstructing the key in memory." />;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Wallet creation — client mode (SSS key-split in iframe)
// ---------------------------------------------------------------------------

async function createWalletFlow(
  setState: (s: IframeState) => void,
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
  identity: { userIdentityID: string; email?: string },
  supportedCurves: Curve[] = ['secp256k1'],
) {
  const token = readSessionToken(ctx.organizationId);
  if (!token) {
    setState({ phase: 'error', message: 'session expired' });
    return;
  }
  const apiCtx = { publishableKey: ctx.publishableKey, token };
  let primaryAddress = '';
  let primarySigner: string | undefined;

  for (const curve of supportedCurves) {
    const kp = curve === 'secp256k1' ? generateKeypair() : (() => {
      const ed = generateEd25519Keypair();
      return { privateKey: ed.privateKey, publicKey: ed.publicKey, address: solanaAddress(ed.publicKey) };
    })();
    try {
      const shares = await splitKey(kp.privateKey, 2, 3);
      const isSecp = curve === 'secp256k1';
      const res = await api.createWallet(apiCtx, {
        address: kp.address,
        public_key: '0x' + bytesToHex(kp.publicKey),
        chain_type: isSecp ? 'evm' : curve,
        curve,
        provider_share: base64Encode(shares[1]),
        provider_share_index: 2,
        recovery_share: base64Encode(shares[2]),
        recovery_share_index: 3,
        account_type: 'eoa',
      });
      writeDeviceShare(ctx.organizationId, identity.userIdentityID, base64Encode(shares[0]), curve);
      if (isSecp || !primaryAddress) {
        primaryAddress = res.address;
        primarySigner = res.signer_address;
      }
      wipe(shares[0]); wipe(shares[1]); wipe(shares[2]); wipe(kp.privateKey);
    } catch (e) {
      wipe(kp.privateKey);
      setState({ phase: 'error', message: e instanceof Error ? e.message : `wallet creation failed (${curve})` });
      return;
    }
  }
  setState({ phase: 'idle', ctx, identity, address: primaryAddress, signerAddress: primarySigner });
}

// ---------------------------------------------------------------------------
// Wallet creation — TEE mode (backend generates keys inside the TEE)
// ---------------------------------------------------------------------------

async function createWalletFlowTEE(
  setState: (s: IframeState) => void,
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
  identity: { userIdentityID: string; email?: string },
) {
  const token = readSessionToken(ctx.organizationId);
  if (!token) {
    setState({ phase: 'error', message: 'session expired' });
    return;
  }
  const apiCtx = { publishableKey: ctx.publishableKey, token };
  try {
    // TEE mode: the backend generates the key inside the TEE and keeps both
    // shares. The iframe does not receive or store any key material.
    const res = await api.createWalletTEE(apiCtx);
    setState({
      phase: 'idle', ctx, identity,
      address: res.address,
      signerAddress: res.signer_address,
    });
  } catch (e) {
    setState({ phase: 'error', message: e instanceof Error ? e.message : 'wallet creation failed (TEE)' });
  }
}

// ---------------------------------------------------------------------------
// Recovery flow (client mode only — TEE wallets do not need device-share
// recovery since the TEE holds all shares)
// ---------------------------------------------------------------------------

async function runRecoveryFlow(
  setState: (s: IframeState) => void,
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
  identity: { userIdentityID: string; email?: string },
  address: string,
  processId: string,
  code: string,
) {
  const token = readSessionToken(ctx.organizationId);
  if (!token) throw new Error('session expired');

  const apiCtx = { publishableKey: ctx.publishableKey, token };
  const verified = await api.verifyRecovery(apiCtx, processId, code);

  setState({ phase: 'recovery_re_sharing', ctx, identity, address });

  const providerShare = base64Decode(verified.provider_share);
  const recoveryShare = base64Decode(verified.recovery_share);

  let privateKey: Uint8Array | null = null;
  try {
    privateKey = await combineShares([providerShare, recoveryShare]);
    // Re-share with a new polynomial to invalidate any compromised old shares.
    const newShares = await splitKey(privateKey, 2, 3);
    const newDevice = newShares[0];
    const newProvider = newShares[1];
    const newRecovery = newShares[2];

    await api.completeRecovery(apiCtx, processId, {
      new_provider_share: base64Encode(newProvider),
      new_provider_share_index: 2,
      new_recovery_share: base64Encode(newRecovery),
      new_recovery_share_index: 3,
    });

    writeDeviceShare(ctx.organizationId, identity.userIdentityID, base64Encode(newDevice));
    wipe(newDevice);
    wipe(newProvider);
    wipe(newRecovery);

    setState({ phase: 'idle', ctx, identity, address });
  } finally {
    if (privateKey) wipe(privateKey);
    wipe(providerShare);
    wipe(recoveryShare);
  }
}

// ---------------------------------------------------------------------------
// Signing — dispatches between client-side reconstruction and TEE
// ---------------------------------------------------------------------------

async function signWithReconstruction(
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
  identity: { userIdentityID: string },
  pending: PendingSign,
  expectedSigner?: string,
  curve: Curve = 'secp256k1',
): Promise<{ signature: string; digest?: string }> {
  if (orgSigningMode === 'tee') {
    return signViaTEE(ctx, pending, curve);
  }
  return signWithClientReconstruction(ctx, identity, pending, expectedSigner, curve);
}

// ---------------------------------------------------------------------------
// TEE signing: the TEE holds both shares, iframe sends a lightweight request
// ---------------------------------------------------------------------------

async function signViaTEE(
  ctx: { publishableKey: string; organizationId: string },
  pending: PendingSign,
  curve: Curve = 'secp256k1',
): Promise<{ signature: string; digest?: string }> {
  const token = readSessionToken(ctx.organizationId);
  if (!token) throw new Error('session expired');

  const apiCtx = { publishableKey: ctx.publishableKey, token };

  if (pending.kind === 'typedData') {
    const digestBytes = hashTypedData(pending.typedData);
    const result = await api.sign(apiCtx, {
      sign_type: 'typedData',
      message: '0x' + bytesToHex(digestBytes),
      curve,
    });
    return { signature: result.signature, digest: result.digest ?? ('0x' + bytesToHex(digestBytes)) };
  }

  if (pending.kind === 'rawTx') {
    throw new Error('TEE rawTx signing is not supported via signWithReconstruction; use runSendTransaction');
  }
  if (pending.kind === 'userOp') {
    throw new Error('TEE userOp signing is not supported via signWithReconstruction; use runUserOpTransaction');
  }
  if (pending.kind === 'wc') {
    throw new Error('TEE WC signing is not supported via signWithReconstruction; use runWCRequest');
  }

  // Message signing
  const network = ('network' in pending && pending.network) ? pending.network : 'ethereum';
  const result = await api.sign(apiCtx, {
    sign_type: 'message',
    network,
    message: pending.message,
    curve,
  });
  return { signature: result.signature, digest: result.digest };
}

// ---------------------------------------------------------------------------
// Client-side SSS reconstruction signing (original flow)
// ---------------------------------------------------------------------------

async function signWithClientReconstruction(
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
  identity: { userIdentityID: string },
  pending: PendingSign,
  expectedSigner?: string,
  curve: Curve = 'secp256k1',
): Promise<{ signature: string; digest?: string }> {
  const token = readSessionToken(ctx.organizationId);
  if (!token) throw new Error('session expired');

  const deviceShareB64 = readDeviceShare(ctx.organizationId, identity.userIdentityID, curve);
  if (!deviceShareB64) {
    clearDeviceShare(ctx.organizationId, identity.userIdentityID, curve);
    throw new Error('device share missing');
  }
  const deviceShare = base64Decode(deviceShareB64);

  const providerRes = await api.getProviderShare({ publishableKey: ctx.publishableKey, token }, curve);
  const providerShare = base64Decode(providerRes.provider_share);

  let privateKey: Uint8Array | null = null;
  try {
    privateKey = await combineShares([deviceShare, providerShare]);

    if (curve === 'secp256k1' && expectedSigner) {
      const pub = secp256k1.getPublicKey(privateKey, false);
      const reconstructedAddr = addressFromPublicKey(pub).toLowerCase();
      if (reconstructedAddr !== expectedSigner.toLowerCase()) {
        clearDeviceShare(ctx.organizationId, identity.userIdentityID, curve);
        throw new Error(
          `share mismatch: reconstructed ${reconstructedAddr} but wallet owner is ${expectedSigner}. ` +
            `Cleared the stale device share — please refresh and recover the wallet.`,
        );
      }
    }

    if (pending.kind === 'typedData') {
      const digestBytes = hashTypedData(pending.typedData);
      const signature = signDigest(privateKey, digestBytes);
      return { signature, digest: '0x' + bytesToHex(digestBytes) };
    }
    if (pending.kind === 'rawTx') {
      throw new Error('signWithReconstruction does not handle rawTx; use signRawTxWithReconstruction');
    }
    if (pending.kind === 'userOp') {
      throw new Error('signWithReconstruction does not handle userOp; use signUserOpHashWithReconstruction');
    }
    if (pending.kind === 'wc') {
      throw new Error('signWithReconstruction does not handle wc; use runWCRequest');
    }

    // Message signing: dispatch to the network-specific format.
    const network = ('network' in pending && pending.network) ? pending.network : 'ethereum';
    return { signature: signForNetwork(network, privateKey, pending.message) };
  } finally {
    if (privateKey) wipe(privateKey);
    wipe(deviceShare);
    wipe(providerShare);
  }
}

// runSendTransaction handles the EOA-path send: prepare via backend,
// confirm with the user, sign the EIP-1559 envelope locally with the
// reconstructed key, broadcast back through the backend, return the
// on-chain tx hash.
//
// Notes vs runExecuteBatch:
//   - No paymaster step; the user pays gas from their EOA balance.
//   - No userOpHash; the signing payload is the EIP-1559 envelope hash.
//   - The PendingSign.kind is 'rawTx' — the confirm screen renders the
//     prepared tx (chain, target, value, max cost) rather than a userOp
//     digest, so the user sees what they're actually authorizing.
async function runSendTransaction(
  req: {
    requestId: string;
    chain: string;
    to: string;
    data?: string;
    value?: string;
    gasLimit?: string;
    preview?: { title: string; description?: string; fields?: { label: string; value: string }[] };
  },
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
  identity: { userIdentityID: string; email?: string },
  address: string,
  signerAddress: string | undefined,
  setState: (s: IframeState) => void,
  pendingRef: { current: Map<string, PendingSign> },
): Promise<ResponseMessage> {
  try {
    const token = readSessionToken(ctx.organizationId);
    if (!token) throw new Error('session expired');
    const apiCtx = { publishableKey: ctx.publishableKey, token };

    // Lane selection. If the user provisioned a smart account on this
    // chain (via enableSmartAccount), every sendTransaction goes through
    // the sponsored 4337 lane. Otherwise we stay on the EOA path the
    // wallet had pre-Chunk-B. The check is one cheap GET; failure here
    // (network blip, etc) doesn't block the EOA fallback.
    const sa = await findSmartAccountForChain(apiCtx, req.chain);
    if (sa) {
      return runUserOpTransaction(req, sa, ctx, identity, address, setState, pendingRef);
    }

    const prepared = await api.prepareEvmTx(apiCtx, {
      chain: req.chain,
      to: req.to,
      data: req.data,
      value: req.value,
      gas_limit: req.gasLimit,
    });

    // Wait for user approval via the confirm screen.
    await new Promise<void>((resolve, reject) => {
      const pending: PendingSign = {
        requestId: req.requestId,
        kind: 'rawTx',
        tx: {
          chain: prepared.chain,
          chainId: prepared.chain_id,
          from: prepared.from,
          to: prepared.to,
          data: prepared.data,
          value: prepared.value,
          gasLimit: prepared.gas_limit,
          maxFeePerGas: prepared.max_fee_per_gas,
          maxPriorityFeePerGas: prepared.max_priority_fee_per_gas,
          nonce: prepared.nonce,
        },
        preview: req.preview,
        // The Confirm screen calls resolve({signature, digest}) on approve
        // (mirroring the sign flows). For rawTx we don't actually use that
        // signature object — the signing happens after approval in the
        // signing phase — so we just acknowledge.
        resolve: () => resolve(),
        reject: (reason) => reject(new Error(reason)),
      };
      pendingRef.current.set(req.requestId, pending);
      setState({ phase: 'confirming', ctx, identity, address, signerAddress, pending });
    });

    setState({
      phase: 'signing', ctx, identity, address, signerAddress,
      pending: {
        requestId: req.requestId, kind: 'rawTx',
        tx: {
          chain: prepared.chain,
          chainId: prepared.chain_id,
          from: prepared.from,
          to: prepared.to,
          data: prepared.data,
          value: prepared.value,
          gasLimit: prepared.gas_limit,
          maxFeePerGas: prepared.max_fee_per_gas,
          maxPriorityFeePerGas: prepared.max_priority_fee_per_gas,
          nonce: prepared.nonce,
        },
        preview: req.preview,
        resolve: () => {}, reject: () => {},
      },
    });

    // Reconstruct the EOA key in memory, sign the envelope, wipe the buffer.
    const signed = await signRawTxWithReconstruction(ctx, identity, prepared, signerAddress);

    const submitted = await api.broadcastEvmTx(apiCtx, {
      chain: prepared.chain,
      raw_tx: signed.rawTxHex,
    });

    return {
      type: 'transaction_sent',
      requestId: req.requestId,
      chain: submitted.chain,
      chainId: submitted.chain_id,
      txHash: submitted.tx_hash,
      from: prepared.from,
    };
  } catch (e) {
    return {
      type: 'error',
      requestId: req.requestId,
      code: 'send_transaction_failed',
      message: e instanceof Error ? e.message : String(e),
    };
  } finally {
    setState({ phase: 'idle', ctx, identity, address, signerAddress });
  }
}

// ----- Smart account provisioning -----
//
// Both endpoints proxy straight to the Sigil backend; the iframe only
// adds the bearer token (session JWT) it already holds. No crypto runs
// here — the SA address comes from the LightAccount factory on-chain
// and is stored server-side. The iframe's role is to be the single
// origin that holds the user's session, so the parent app never sees it.

async function runEnableSmartAccount(
  req: { requestId: string; chain: string },
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
): Promise<ResponseMessage> {
  try {
    const token = readSessionToken(ctx.organizationId);
    if (!token) throw new Error('session expired');
    const apiCtx = { publishableKey: ctx.publishableKey, token };
    const dto = await api.createSmartAccount(apiCtx, { chain: req.chain });
    return { type: 'smart_account', requestId: req.requestId, account: smartAccountFromDTO(dto) };
  } catch (e) {
    return errorFromException(req.requestId, 'enable_smart_account_failed', e);
  }
}

async function runListSmartAccounts(
  req: { requestId: string },
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
): Promise<ResponseMessage> {
  try {
    const token = readSessionToken(ctx.organizationId);
    if (!token) throw new Error('session expired');
    const apiCtx = { publishableKey: ctx.publishableKey, token };
    const res = await api.listSmartAccounts(apiCtx);
    return {
      type: 'smart_accounts',
      requestId: req.requestId,
      accounts: res.items.map(smartAccountFromDTO),
    };
  } catch (e) {
    return errorFromException(req.requestId, 'list_smart_accounts_failed', e);
  }
}

// ----- WalletConnect shell-driven calls -----
//
// The shell (parent app / playground) can't call /v1/wallets/me/wc/*
// directly because the user's JWT lives only in this iframe. These
// three runners proxy the calls through the iframe's session, mirror
// of the enableSmartAccount / listSmartAccounts pattern.

async function runPairWC(
  req: { requestId: string; uri: string },
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
): Promise<ResponseMessage> {
  try {
    const token = readSessionToken(ctx.organizationId);
    if (!token) throw new Error('session expired');
    const apiCtx = { publishableKey: ctx.publishableKey, token };
    const out = await apiPairWCFromShell(apiCtx, req.uri);
    return {
      type: 'wc_paired',
      requestId: req.requestId,
      pairingTopic: out.pairing_topic,
      walletId: out.wallet_id,
    };
  } catch (e) {
    return errorFromException(req.requestId, 'pair_wc_failed', e);
  }
}

async function runListWCSessions(
  req: { requestId: string },
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
): Promise<ResponseMessage> {
  try {
    const token = readSessionToken(ctx.organizationId);
    if (!token) throw new Error('session expired');
    const apiCtx = { publishableKey: ctx.publishableKey, token };
    const res = await apiListWCSessions(apiCtx);
    return {
      type: 'wc_sessions',
      requestId: req.requestId,
      sessions: res.items.map(wcSessionFromDTO),
    };
  } catch (e) {
    return errorFromException(req.requestId, 'list_wc_sessions_failed', e);
  }
}

async function runDisconnectWC(
  req: { requestId: string; topic: string },
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
): Promise<ResponseMessage> {
  try {
    const token = readSessionToken(ctx.organizationId);
    if (!token) throw new Error('session expired');
    const apiCtx = { publishableKey: ctx.publishableKey, token };
    await apiDisconnectWCSession(apiCtx, req.topic);
    return { type: 'wc_disconnected', requestId: req.requestId };
  } catch (e) {
    return errorFromException(req.requestId, 'disconnect_wc_failed', e);
  }
}

function wcSessionFromDTO(dto: WCSessionDTO) {
  return {
    topic: dto.topic,
    walletId: dto.wallet_id,
    peerName: dto.peer_name,
    peerUrl: dto.peer_url,
    peerIcon: dto.peer_icon,
    chains: dto.chains,
    status: dto.status,
    createdAt: dto.created_at,
  };
}

// findSmartAccountForChain returns the SA on the requested chain or null
// when none is provisioned. Used by runSendTransaction to pick between
// the sponsored 4337 lane and the raw EOA lane. Errors are swallowed
// (the caller falls back to EOA on failure) so a transient API issue
// can't lock the user out of sending.
async function findSmartAccountForChain(
  apiCtx: { publishableKey: string; token: string },
  chain: string,
): Promise<{ address: string; chain: string; ownerAddress: string } | null> {
  try {
    const res = await api.listSmartAccounts(apiCtx);
    const match = res.items.find((sa) => sa.chain.toLowerCase() === chain.toLowerCase());
    return match
      ? { address: match.address, chain: match.chain, ownerAddress: match.owner_address }
      : null;
  } catch {
    return null;
  }
}

// runUserOpTransaction is the sponsored 4337 lane: build via the backend
// (which simulates + sponsors via Pimlico), confirm with the user,
// reconstruct the EOA key in iframe memory, sign the EIP-191-wrapped
// userOpHash, submit, poll until the bundler reports the on-chain tx
// hash, return.
async function runUserOpTransaction(
  req: {
    requestId: string;
    chain: string;
    to: string;
    data?: string;
    value?: string;
    preview?: { title: string; description?: string; fields?: { label: string; value: string }[] };
  },
  sa: { address: string; chain: string; ownerAddress: string },
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
  identity: { userIdentityID: string; email?: string },
  address: string,
  setState: (s: IframeState) => void,
  pendingRef: { current: Map<string, PendingSign> },
): Promise<ResponseMessage> {
  const signerAddress = sa.ownerAddress;
  try {
    const token = readSessionToken(ctx.organizationId);
    if (!token) throw new Error('session expired');
    const apiCtx = { publishableKey: ctx.publishableKey, token };

    const calls = [{ to: req.to, data: req.data ?? '0x', value: req.value }];
    const built = await api.userOpBuild(apiCtx, {
      chain: req.chain,
      calls,
      preview: req.preview,
    });

    // Wait for user approval. The confirm screen renders the SA address +
    // calls with a "sponsored by Sigil" note (no fee fields — the user
    // pays nothing).
    await new Promise<void>((resolve, reject) => {
      const pending: PendingSign = {
        requestId: req.requestId,
        kind: 'userOp',
        op: {
          chain: req.chain,
          sender: sa.address,
          owner: sa.ownerAddress,
          userOpHash: built.user_op_hash,
          calls,
        },
        preview: built.preview ?? req.preview,
        resolve: () => resolve(),
        reject: (reason) => reject(new Error(reason)),
      };
      pendingRef.current.set(req.requestId, pending);
      setState({ phase: 'confirming', ctx, identity, address, signerAddress, pending });
    });

    setState({
      phase: 'signing', ctx, identity, address, signerAddress,
      pending: {
        requestId: req.requestId, kind: 'userOp',
        op: {
          chain: req.chain,
          sender: sa.address,
          owner: sa.ownerAddress,
          userOpHash: built.user_op_hash,
          calls,
        },
        preview: built.preview ?? req.preview,
        resolve: () => {}, reject: () => {},
      },
    });

    const sig = await signUserOpHashWithReconstruction(ctx, identity, built.user_op_hash, signerAddress);

    let submitted = await api.userOpSubmit(apiCtx, { user_op_hash: built.user_op_hash, signature: sig });

    // Bundler accepted but not yet landed: poll the get-endpoint, which
    // re-queries Pimlico. Cap at 60s — the SDK consumer wants a tx hash,
    // not a "still pending". Beyond that, surface what we have so the
    // caller can decide.
    const deadline = Date.now() + 60_000;
    while (submitted.status !== 'confirmed' && submitted.status !== 'failed' && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2_000));
      submitted = await api.userOpGet(apiCtx, built.user_op_hash);
    }

    if (!submitted.tx_hash) {
      throw new Error(`userop ${submitted.status} — no tx hash yet, retry later`);
    }
    return {
      type: 'transaction_sent',
      requestId: req.requestId,
      chain: req.chain,
      chainId: '',
      txHash: submitted.tx_hash,
      from: sa.address,
    };
  } catch (e) {
    return errorFromException(req.requestId, 'send_transaction_failed', e);
  } finally {
    setState({ phase: 'idle', ctx, identity, address, signerAddress });
  }
}

// runWCRequest is the WalletConnect end of the iframe. The SSE
// listener calls it whenever a wc_signing_requests row lands; we
// route into the same confirm UI the parent SDK uses, sign per
// method, POST the result back, and let the sidecar's long-poll
// resolve.
//
// Routing per method:
//   personal_sign         — req.params.message is the UTF-8 string the
//                           backend already decoded from hex. We do
//                           the EIP-191 wrap + keccak + sign locally,
//                           same as signMessage("ethereum").
//   eth_sign              — req.params.digest_hex is a raw 32-byte
//                           digest. Sign as-is (this is the
//                           dangerous legacy method).
//   eth_signTypedData_v4  — same shape; the sidecar already hashed.
//   anything else         — auto-reject with "unsupported in iframe";
//                           the dApp gets a clean JSON-RPC error.
async function runWCRequest(
  req: WCSigningRequestEvent,
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
  pendingRef: { current: Map<string, PendingSign> },
  setState: (s: IframeState) => void,
  stateRef: { current: IframeState },
): Promise<void> {
  // Resolve auth from session token (the SSE callback runs outside
  // the listener context, so the parent-supplied JWT lives only in
  // localStorage).
  const token = readSessionToken(ctx.organizationId);
  if (!token) return; // session expired; the SSE will reconnect post-login

  const cur = stateRef.current;
  if (!(cur.phase === 'idle')) {
    // serialiseSign already waited for idle, but a race is possible.
    // Best-effort reject so the sidecar's long-poll resolves quickly
    // instead of timing out.
    try {
      await api.rejectWCRequest({ publishableKey: ctx.publishableKey, token }, req.id, 'iframe_busy');
    } catch { /* nothing useful to do */ }
    return;
  }
  const identity = cur.identity;
  const address = cur.address;
  const signerAddress = cur.signerAddress;

  // Methods we don't (yet) handle in the iframe. eth_sendTransaction
  // routing is Chunk E; wallet_switchEthereumChain is handled
  // server-side and shouldn't reach here.
  if (req.method !== 'personal_sign' && req.method !== 'eth_sign' && req.method !== 'eth_signTypedData_v4') {
    try {
      await api.rejectWCRequest({ publishableKey: ctx.publishableKey, token }, req.id, 'method_not_supported_iframe',
        `${req.method} is not signed in the iframe (Chunk E or server-only)`);
    } catch { /* nothing useful to do */ }
    return;
  }

  // Surface the confirm screen + wait for the user.
  let approved = false;
  let rejectReason = 'user_rejected';
  try {
    await new Promise<void>((resolve, reject) => {
      const pending: PendingSign = {
        requestId: req.id,
        kind: 'wc',
        wc: {
          id: req.id,
          topic: req.topic,
          method: req.method as 'personal_sign' | 'eth_sign' | 'eth_signTypedData_v4',
          message: req.params.message,
          digestHex: req.params.digest_hex,
        },
        resolve: () => resolve(),
        reject: (reason) => reject(new Error(reason)),
      };
      pendingRef.current.set(req.id, pending);
      setState({ phase: 'confirming', ctx, identity, address, signerAddress, pending });
    });
    approved = true;
  } catch (e) {
    approved = false;
    rejectReason = e instanceof Error ? e.message : 'user_rejected';
  }

  if (!approved) {
    try {
      await api.rejectWCRequest({ publishableKey: ctx.publishableKey, token }, req.id, rejectReason);
    } catch { /* ignore */ }
    setState({ phase: 'idle', ctx, identity, address, signerAddress });
    return;
  }

  // Sign locally.
  setState({
    phase: 'signing', ctx, identity, address, signerAddress,
    pending: {
      requestId: req.id, kind: 'wc',
      wc: {
        id: req.id,
        topic: req.topic,
        method: req.method as 'personal_sign' | 'eth_sign' | 'eth_signTypedData_v4',
        message: req.params.message,
        digestHex: req.params.digest_hex,
      },
      resolve: () => {}, reject: () => {},
    },
  });

  try {
    const signature = await signWCWithReconstruction(ctx, identity, req, signerAddress ?? address);
    await api.completeWCRequest({ publishableKey: ctx.publishableKey, token }, req.id, signature);
  } catch (e) {
    try {
      await api.rejectWCRequest({ publishableKey: ctx.publishableKey, token }, req.id,
        'sign_failed', e instanceof Error ? e.message : String(e));
    } catch { /* ignore */ }
  } finally {
    setState({ phase: 'idle', ctx, identity, address, signerAddress });
  }
}

// signWCWithReconstruction handles the three iframe-side WC methods:
//   personal_sign         — EIP-191 wrap of UTF-8 message, keccak,
//                           secp256k1 over the digest.
//   eth_sign              — raw 32-byte digest signing.
//   eth_signTypedData_v4  — same as eth_sign; the sidecar pre-hashed.
// Returns the 65-byte 0x-prefixed r||s||v signature.
async function signWCWithReconstruction(
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
  identity: { userIdentityID: string },
  req: WCSigningRequestEvent,
  expectedSigner: string,
): Promise<string> {
  const token = readSessionToken(ctx.organizationId);
  if (!token) throw new Error('session expired');

  const deviceShareB64 = readDeviceShare(ctx.organizationId, identity.userIdentityID);
  if (!deviceShareB64) {
    clearDeviceShare(ctx.organizationId, identity.userIdentityID);
    throw new Error('device share missing');
  }
  const deviceShare = base64Decode(deviceShareB64);

  const providerRes = await api.getProviderShare({ publishableKey: ctx.publishableKey, token });
  const providerShare = base64Decode(providerRes.provider_share);

  let privateKey: Uint8Array | null = null;
  try {
    privateKey = await combineShares([deviceShare, providerShare]);
    const pub = secp256k1.getPublicKey(privateKey, false);
    const reconstructedAddr = addressFromPublicKey(pub).toLowerCase();
    if (reconstructedAddr !== expectedSigner.toLowerCase()) {
      clearDeviceShare(ctx.organizationId, identity.userIdentityID);
      throw new Error(`share mismatch: reconstructed ${reconstructedAddr} but wallet owner is ${expectedSigner}.`);
    }

    if (req.method === 'personal_sign') {
      const msg = req.params.message ?? '';
      return signForNetwork('ethereum', privateKey, msg);
    }
    // eth_sign and eth_signTypedData_v4 both arrive with digest_hex.
    const digestHex = req.params.digest_hex ?? '';
    const digest = hexToBytes(digestHex);
    if (digest.length !== 32) throw new Error('digest must be 32 bytes');
    return signDigest(privateKey, digest);
  } finally {
    if (privateKey) wipe(privateKey);
    wipe(deviceShare);
    wipe(providerShare);
  }
}

// signUserOpHashWithReconstruction reconstructs the EOA private key in
// iframe memory, signs the EIP-191-wrapped 32-byte userOpHash, wipes,
// returns the 65-byte 0x-prefixed signature. The backend prepends the
// LightAccount v2 owner type byte (0x00); we send raw r||s||v.
async function signUserOpHashWithReconstruction(
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
  identity: { userIdentityID: string },
  userOpHash: string,
  expectedSigner: string,
): Promise<string> {
  const token = readSessionToken(ctx.organizationId);
  if (!token) throw new Error('session expired');

  const deviceShareB64 = readDeviceShare(ctx.organizationId, identity.userIdentityID);
  if (!deviceShareB64) {
    clearDeviceShare(ctx.organizationId, identity.userIdentityID);
    throw new Error('device share missing');
  }
  const deviceShare = base64Decode(deviceShareB64);

  const providerRes = await api.getProviderShare({ publishableKey: ctx.publishableKey, token });
  const providerShare = base64Decode(providerRes.provider_share);

  let privateKey: Uint8Array | null = null;
  try {
    privateKey = await combineShares([deviceShare, providerShare]);
    const pub = secp256k1.getPublicKey(privateKey, false);
    const reconstructedAddr = addressFromPublicKey(pub).toLowerCase();
    if (reconstructedAddr !== expectedSigner.toLowerCase()) {
      clearDeviceShare(ctx.organizationId, identity.userIdentityID);
      throw new Error(
        `share mismatch: reconstructed ${reconstructedAddr} but smart-account owner is ${expectedSigner}. ` +
          'Cleared the stale device share — please refresh and recover the wallet.',
      );
    }
    // EIP-191 wrap the 32-byte userOpHash, then sign the resulting keccak.
    const hashBytes = hexToBytes(userOpHash);
    const prefix = new TextEncoder().encode('\x19Ethereum Signed Message:\n32');
    const buf = new Uint8Array(prefix.length + hashBytes.length);
    buf.set(prefix, 0);
    buf.set(hashBytes, prefix.length);
    const digest = keccak_256(buf);
    return signDigest(privateKey, digest);
  } finally {
    if (privateKey) wipe(privateKey);
    wipe(deviceShare);
    wipe(providerShare);
  }
}

function smartAccountFromDTO(dto: {
  id: string; wallet_id: string; owner_address: string; chain: string; factory: string;
  address: string; deployed: boolean; created_by: string; created_at: string;
}) {
  return {
    id: dto.id,
    walletId: dto.wallet_id,
    ownerAddress: dto.owner_address,
    chain: dto.chain,
    factory: dto.factory,
    address: dto.address,
    deployed: dto.deployed,
    createdBy: dto.created_by,
    createdAt: dto.created_at,
  };
}

function errorFromException(requestId: string, code: string, e: unknown): ResponseMessage {
  if (e instanceof ApiError) {
    return { type: 'error', requestId, code: e.code, message: e.message };
  }
  return { type: 'error', requestId, code, message: e instanceof Error ? e.message : String(e) };
}

async function signRawTxWithReconstruction(
  ctx: { publishableKey: string; organizationId: string; authMode: 'oidc' | 'sigil' },
  identity: { userIdentityID: string },
  prepared: { chain: string; chain_id: string; nonce: string; from: string; to: string; data: string; value: string; gas_limit: string; max_fee_per_gas: string; max_priority_fee_per_gas: string },
  expectedSigner?: string,
) {
  const token = readSessionToken(ctx.organizationId);
  if (!token) throw new Error('session expired');

  const deviceShareB64 = readDeviceShare(ctx.organizationId, identity.userIdentityID);
  if (!deviceShareB64) {
    clearDeviceShare(ctx.organizationId, identity.userIdentityID);
    throw new Error('device share missing');
  }
  const deviceShare = base64Decode(deviceShareB64);

  const providerRes = await api.getProviderShare({ publishableKey: ctx.publishableKey, token });
  const providerShare = base64Decode(providerRes.provider_share);

  let privateKey: Uint8Array | null = null;
  try {
    privateKey = await combineShares([deviceShare, providerShare]);

    // Same defence as in signWithReconstruction: a mismatch means the
    // device share is stale (from a prior wallet); wipe locally so the
    // next attempt goes through recovery instead of producing a junk
    // signature that the RPC would reject as `invalid signature`.
    const expected = expectedSigner ?? prepared.from;
    const pub = secp256k1.getPublicKey(privateKey, false);
    const reconstructedAddr = addressFromPublicKey(pub).toLowerCase();
    if (reconstructedAddr !== expected.toLowerCase()) {
      clearDeviceShare(ctx.organizationId, identity.userIdentityID);
      throw new Error(
        `share mismatch: reconstructed ${reconstructedAddr} but wallet owner is ${expected}. ` +
          'Cleared the stale device share — please refresh and recover the wallet.',
      );
    }

    return signEip1559Tx(privateKey, prepared);
  } finally {
    if (privateKey) wipe(privateKey);
    wipe(deviceShare);
    wipe(providerShare);
  }
}

function CenteredMessage({ title, body, tone }: { title: string; body: string; tone?: 'error' }) {
  return (
    <main className="flex min-h-screen items-center justify-center px-6">
      <div className="max-w-md text-center">
        <h1 className={`text-xl font-semibold tracking-tightish ${tone === 'error' ? 'text-bad' : 'text-ink-primary'}`}>
          {title}
        </h1>
        <p className="mt-2 text-sm text-ink-secondary">{body}</p>
      </div>
    </main>
  );
}
